/**
 * <amq-thumbnail-arrows>: scroll buttons for Horizon's product-gallery thumbnail strips
 * (snippets/amq_thumbnail_arrows.liquid).
 *
 * Finds every thumbnail strip in the surrounding media column, clones the two buttons from its own
 * <template> into the strip's slideshow-controls element and scrolls the strip by 80 % of its width
 * per click. The buttons hide while the strip does not overflow and disable at either end.
 *
 * Switching to another combined-listing product morphs the whole <main> with server HTML, which drops
 * the injected buttons (and may replace the strips) while this element stays connected. A
 * MutationObserver on the media column therefore re-attaches the buttons whenever they go missing.
 * All listeners and observers are removed in disconnectedCallback.
 */
class AmqThumbnailArrows extends HTMLElement {
  /** @type {Map<HTMLElement, { update: () => void, isIntact: () => boolean, cleanup: () => void }>} */
  #strips = new Map();

  /** @type {MutationObserver | undefined} */
  #observer;

  #frame = 0;

  connectedCallback() {
    const template = this.querySelector('template');
    const scope = this.closest('.product-information__media') ?? this.parentElement;
    if (!(template instanceof HTMLTemplateElement) || !scope) return;

    this.#sync(scope, template);

    this.#observer = new MutationObserver(() => {
      if (this.#frame) return;
      this.#frame = requestAnimationFrame(() => {
        this.#frame = 0;
        this.#sync(scope, template);
      });
    });
    this.#observer.observe(scope, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = undefined;
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    for (const strip of this.#strips.values()) strip.cleanup();
    this.#strips.clear();
  }

  /**
   * Drops strips that were replaced or lost their buttons, attaches buttons to new strips and
   * refreshes the state of the rest (the thumbnail count may have changed).
   * @param {Element} scope - The media column
   * @param {HTMLTemplateElement} template - Holds the two buttons
   */
  #sync(scope, template) {
    for (const [container, strip] of this.#strips) {
      if (!strip.isIntact()) {
        strip.cleanup();
        this.#strips.delete(container);
      }
    }

    for (const container of scope.querySelectorAll('slideshow-controls[thumbnails] .slideshow-controls__thumbnails-container')) {
      if (!(container instanceof HTMLElement)) continue;
      const strip = this.#strips.get(container);
      if (strip) {
        strip.update();
      } else {
        this.#attach(container, template);
      }
    }
  }

  /**
   * @param {HTMLElement} container - The scrolling thumbnails container
   * @param {HTMLTemplateElement} template - Holds the two buttons
   */
  #attach(container, template) {
    const host = container.closest('slideshow-controls');
    if (!(host instanceof HTMLElement)) return;

    // Leftover buttons from before a morph are not wired up any more.
    for (const stale of host.querySelectorAll(':scope > .amq-thumb-arrow')) stale.remove();

    const fragment = template.content.cloneNode(true);
    const buttons = /** @type {NodeListOf<HTMLButtonElement>} */ (fragment.querySelectorAll('button'));
    const [previous, next] = buttons;
    if (!previous || !next) return;

    host.append(fragment);
    host.classList.add('amq-has-thumb-arrows');

    const step = () => Math.max(container.clientWidth * 0.8, 1);
    const scrollPrevious = () => container.scrollBy({ left: -step(), behavior: 'smooth' });
    const scrollNext = () => container.scrollBy({ left: step(), behavior: 'smooth' });
    const update = () => {
      const maxScroll = container.scrollWidth - container.clientWidth;
      const overflows = maxScroll > 2;
      previous.hidden = !overflows;
      next.hidden = !overflows;
      previous.disabled = container.scrollLeft <= 1;
      next.disabled = container.scrollLeft >= maxScroll - 1;
    };
    const isIntact = () =>
      container.isConnected &&
      container.closest('slideshow-controls') === host &&
      previous.parentElement === host &&
      next.parentElement === host &&
      host.classList.contains('amq-has-thumb-arrows');

    previous.addEventListener('click', scrollPrevious);
    next.addEventListener('click', scrollNext);
    container.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(container);
    update();

    this.#strips.set(container, {
      update,
      isIntact,
      cleanup: () => {
        previous.removeEventListener('click', scrollPrevious);
        next.removeEventListener('click', scrollNext);
        container.removeEventListener('scroll', update);
        observer.disconnect();
        previous.remove();
        next.remove();
        host.classList.remove('amq-has-thumb-arrows');
      },
    });
  }
}

if (!customElements.get('amq-thumbnail-arrows')) {
  customElements.define('amq-thumbnail-arrows', AmqThumbnailArrows);
}
